import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Close,
  ArrowForward,
  ArrowBack,
  Groups,
  Star,
  StarBorder,
} from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import {
  professionalTeamService,
  TeamMemberItem,
} from "../../services/professionalTeamService";
import { colors } from "../../theme/designSystem";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamTitle: string;
}

// Composição da equipe (UEPRs): mesmo padrão do modal de tipos profissionais —
// dois painéis com setas de vincular/desvincular; cada clique persiste na hora
// (escrita dupla no backend: CSV local + lista no Bubble).
const TeamMembersModal: React.FC<Props> = ({ isOpen, onClose, teamId, teamTitle }) => {
  const { toast, showToast, hideToast } = useToast();
  const [linked, setLinked] = useState<TeamMemberItem[]>([]);
  const [available, setAvailable] = useState<TeamMemberItem[]>([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!teamId) return;
    setBusy(true);
    try {
      const data = await professionalTeamService.getMembers(teamId);
      setLinked(data.linked);
      setAvailable(data.available);
    } catch (error) {
      console.error("Erro ao carregar membros:", error);
      showToast("Erro ao carregar os membros da equipe.", "error");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isOpen) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamId]);

  const handleAdd = async (item: TeamMemberItem) => {
    if (busy) return;
    setBusy(true);
    try {
      await professionalTeamService.addMember(teamId, item.ueprId);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao incluir o profissional na equipe.";
      showToast(message, "error");
      setBusy(false);
    }
  };

  const handleRemove = async (item: TeamMemberItem) => {
    if (busy) return;
    setBusy(true);
    try {
      await professionalTeamService.removeMember(teamId, item.ueprId);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao remover o profissional da equipe.";
      showToast(message, "error");
      setBusy(false);
    }
  };

  // Coordenação única: marcar outro membro transfere a coordenação; clicar no atual limpa.
  const handleToggleCoordinator = async (item: TeamMemberItem) => {
    if (busy) return;
    setBusy(true);
    try {
      await professionalTeamService.setCoordinator(
        teamId,
        item.isCoordinator ? null : item.ueprId
      );
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao alterar a coordenação da equipe.";
      showToast(message, "error");
      setBusy(false);
    }
  };

  const label = (item: TeamMemberItem) =>
    item.professionalTypeName ? `${item.name} — ${item.professionalTypeName}` : item.name;

  const listBox = (
    title: string,
    items: TeamMemberItem[],
    side: "available" | "linked"
  ) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        border: `1px solid ${colors.backgroundAlt}`,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          p: 1,
          borderBottom: `1px solid ${colors.backgroundAlt}`,
          color: colors.textSecondary,
        }}
      >
        {title} ({items.length})
      </Typography>
      <Box sx={{ maxHeight: 320, overflowY: "auto", p: 0.5 }}>
        {items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary, p: 1.5, textAlign: "center" }}
          >
            —
          </Typography>
        ) : (
          items.map((item) => (
            <Box
              key={item.ueprId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: "6px",
                "&:hover": { backgroundColor: colors.background },
              }}
            >
              {side === "linked" && (
                <Tooltip title="Remover da equipe">
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(item)}
                    sx={{ color: colors.primary }}
                  >
                    <ArrowBack sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  fontWeight: item.isCoordinator ? 600 : 400,
                }}
              >
                {label(item)}
              </Typography>
              {side === "linked" && (
                <Tooltip
                  title={
                    item.isCoordinator
                      ? "Coordenador(a) da equipe — clique para remover a coordenação"
                      : "Definir como coordenador(a) da equipe"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => handleToggleCoordinator(item)}
                    sx={{ color: item.isCoordinator ? "#f9a825" : colors.textSecondary }}
                  >
                    {item.isCoordinator ? (
                      <Star sx={{ fontSize: "1.1rem" }} />
                    ) : (
                      <StarBorder sx={{ fontSize: "1.1rem" }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {side === "available" && (
                <Tooltip title="Incluir na equipe">
                  <IconButton
                    size="small"
                    onClick={() => handleAdd(item)}
                    sx={{ color: colors.primary }}
                  >
                    <ArrowForward sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={busy} message="Processando..." />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Groups />
          {teamTitle} / Profissionais
        </Box>
        <IconButton
          onClick={onClose}
          disabled={busy}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          Inclua (→) ou remova (←) os profissionais desta equipe. Use a estrela
          para definir quem coordena (uma pessoa por equipe). Cada mudança é
          gravada imediatamente.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          {listBox("Disponíveis", available, "available")}
          {listBox("Na equipe", linked, "linked")}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: "1rem 1.5rem" }}>
        <Button
          onClick={onClose}
          disabled={busy}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Fechar
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default TeamMembersModal;
